import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import {
  GraduationCap, CheckCircle, ArrowRight, CircleNotch, Sparkle,
  MapTrifold, ListChecks, Compass, RocketLaunch, ArrowLeft,
  Lightbulb, Warning, Info, X, Medal, ShareNetwork, Copy,
} from "@phosphor-icons/react";
import FarmersNav from "@/components/FarmersNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import { http } from "@/lib/api";
import { fpPath } from "@/lib/site";
import { toast } from "sonner";

/**
 * FarmersAcademy — Farm Growth Academy Phase 1 hub.
 *
 * A single page with 5 tabs that expose the crash-course, audit,
 * roadmap, 30-day project workspace and AI-driven asset builder.
 * Every action inside the Academy commits work to the same
 * FarmersPulse database, so a lesson doesn't end with a video — it
 * ends with something the farmer can ship.
 */
const TABS = [
  { key: "dashboard",label: "Dashboard",     icon: RocketLaunch },
  { key: "course",   label: "Crash Course",  icon: GraduationCap },
  { key: "audit",    label: "Growth Audit",  icon: ListChecks },
  { key: "roadmap",  label: "Roadmap",       icon: MapTrifold },
  { key: "projects", label: "30-Day Projects", icon: RocketLaunch },
  { key: "builder",  label: "Asset Builder", icon: Sparkle },
  { key: "vault",    label: "Campaign Vault",icon: Compass },
  { key: "calendar", label: "Calendar",      icon: MapTrifold },
  { key: "voice",    label: "Brand Voice",   icon: Sparkle },
  { key: "recommend",label: "What next?",    icon: Lightbulb },
  { key: "journey",  label: "Customer Journey", icon: Compass },
];

export default function FarmersAcademy() {
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "course");
  const [focusTarget, setFocusTarget] = useState(sp.get("focus") || null);
  useEffect(() => { setSp((prev) => { prev.set("tab", tab); return prev; }, { replace: true }); }, [tab, setSp]);

  // Deep-link a lesson's Action to the exact tool/builder/project it needs.
  function goToAction(action) {
    if (!action) return;
    if (action.tool_to) {
      // Customer Journey lives as an Academy tab, not a standalone route.
      if (action.tool_to.includes("customer-journey")) { setTab("journey"); return; }
      window.location.href = fpPath(action.tool_to.replace(/^\/farmers/, ""));
      return;
    }
    if (action.builder) { setFocusTarget(action.builder); setTab("builder"); return; }
    if (action.project_slug) { setFocusTarget(action.project_slug); setTab("projects"); return; }
  }

  return (
    <main
      className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]"
      data-testid="farmers-academy"
      data-translate-target="true"
    >
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        <Link
          to={fpPath("/")}
          className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]"
          data-testid="academy-back"
        >
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>

        <header className="mt-3" data-testid="academy-header">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            FarmersPulse · Farm Growth Academy
          </div>
          <h1 className="mt-1 font-serif-display text-4xl sm:text-5xl leading-[1.02] tracking-tight text-[#151312]">
            Learn it. Build it. Launch it. Measure it.
          </h1>
          <p className="mt-3 text-lg text-[#3A3830] max-w-3xl">
            We help you decide what to work on next, teach you how to do it, give you the tool to build it, help you launch it, and show you whether it worked.
          </p>
        </header>

        <div className="mt-4">
          <ListenTranslateBar
            text="Farm Growth Academy. Learn it. Build it. Launch it. Measure it. We help you decide what to work on next, teach you how to do it, give you the tool to build it, help you launch it, and show you whether it worked."
            testidPrefix="academy-tools"
          />
        </div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="academy-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              data-testid={`academy-tab-${key}`}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${tab === key ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "border-black/15 bg-white text-[#241209] hover:bg-black/5"}`}
            >
              <Icon size={12} weight="fill" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "dashboard" && <DashboardTab onGoto={setTab} />}
          {tab === "course"   && <CourseTab onAction={goToAction} />}
          {tab === "audit"    && <AuditTab onCompleteGoto={setTab} />}
          {tab === "roadmap"  && <RoadmapTab />}
          {tab === "projects" && <ProjectsTab focus={focusTarget} />}
          {tab === "builder"  && <BuilderTab focus={focusTarget} />}
          {tab === "vault"    && <VaultTab />}
          {tab === "calendar" && <CalendarTab />}
          {tab === "voice"    && <BrandVoiceTab />}
          {tab === "recommend"&& <RecommendTab onGoto={setTab} />}
          {tab === "journey"  && <JourneyTab onGoto={setTab} />}
        </div>
      </div>
    </main>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Crash Course
// ══════════════════════════════════════════════════════════════════

// Plain-language "what are we talking about?" explainers, one per lesson.
// Short, practical and action-oriented so a farmer knows what to do and why.
const LESSON_EXPLAINERS = {
  l1: "Think about the people most likely to buy from your farm. Describe who they are, what they buy, where they live, what matters to them, and why they would choose your farm over another seller. Your profile will help you create better products, messages, and promotions.",
  l2: "Customers are not only buying vegetables, fruit, meat, flowers, or other products — they are buying freshness, convenience, trust, local connection, health, quality, or an experience. Write a short story explaining what your farm provides, why you do it, and why customers should care.",
  l3: "Follow a customer from the first time they discover your farm to becoming a repeat buyer. Identify the steps: discover you, learn about you, check products and prices, contact or visit you, purchase, and return. Look for places where you can make the experience easier.",
  l4: "Give potential customers a useful reason to share their email or contact information. This could be a seasonal produce guide, recipe booklet, planting calendar, farm discount, harvest alert, “what’s available this week” list, or first-purchase offer.",
  l5: "Make sure a new visitor can immediately understand what your farm sells, where you are located, what is available, and how to buy. Add clear buttons such as Shop Now, See What’s Available, Visit the Farm, Order for Pickup, Join Our Email List, or Contact Us.",
  l6: "Customers should not have to call or message just to find out what you have. Update your farm catalog with current products, quantities, prices, pickup options, expected harvest dates, sold-out items, and seasonal availability.",
  l7: "Create a short series of messages for people who join your mailing list. Welcome them, introduce your farm, explain what you grow or produce, tell them how to buy, and invite them to follow your seasonal updates and special offers.",
  l8: "Staying in touch helps turn occasional customers into regular customers. Send a simple weekly update with what is fresh, what is coming soon, farm news, recipes, pickup information, special offers, and a clear link or instruction for ordering.",
  l9: "Give customers a timely reason to buy now. Promote seasonal abundance, weekend specials, harvest boxes, bulk discounts, U-pick days, surplus deals, holiday packages, or limited-quantity products. Always make the offer and deadline easy to understand.",
  l10: "Check which marketing activities actually bring customers and sales. Look at website visits, inquiries, email signups, product views, orders, repeat customers, popular products, and where visitors came from. Use this information to spend more time on what works.",
  l11: "Help nearby customers find your farm when they search for local produce, farm stores, U-pick, flowers, meat, eggs, or other products. Add your location, hours, contact information, photos, website, products, and farm description, and keep the information current.",
  l12: "Compare selling directly to consumers with selling to restaurants, stores, schools, distributors, or other wholesale buyers. Consider pricing, volume, packaging, delivery, payment terms, labor, and profit margins. Decide which combination gives your farm the best balance of income, stability, and workload.",
};

const LESSON_SAVE_LINE =
  "Complete the action now, save your work in FarmersPulse, and come back anytime to improve it.";

// Reverse maps: when a farmer actually produces an asset (builder) or starts a
// project that a lesson points to, we auto-tick that lesson complete.
const BUILDER_TO_LESSON = {
  "ideal-customer": "l1",
  "brand-story": "l2",
  "lead-magnet": "l4",
  "weekly-email": "l8",
  "wholesale-intro": "l12",
};
const PROJECT_TO_LESSON = {
  "homepage-makeover": "l5",
  "welcome-email-series": "l7",
  "seasonal-promotion": "l9",
  "google-business-profile": "l11",
};

// Best-effort lesson-completion ping (silent if signed out); rewards the
// farmer with a toast the moment producing an asset auto-ticks a lesson.
function markLessonDone(lessonId) {
  if (!lessonId) return;
  http.post(`/academy/course/${lessonId}/complete`)
    .then(() => toast.success("Lesson complete — nice work!", {
      description: "We saved this to your Marketing Crash Course.",
    }))
    .catch(() => {});
}

function LessonExplainerModal({ lesson, index, onClose, onAction }) {
  if (!lesson) return null;
  const body = LESSON_EXPLAINERS[lesson.id];
  const listenText = `${lesson.title}. ${body} ${LESSON_SAVE_LINE}`;
  return createPortal(
    <div
      data-testid="academy-lesson-explainer"
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
      style={{ height: "100dvh" }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#F5EBDC] border border-black/15 shadow-2xl rounded-2xl overflow-hidden max-h-[90dvh] overflow-y-auto">
        <button
          onClick={onClose}
          data-testid="academy-lesson-explainer-close"
          className="absolute top-4 right-4 p-1 text-[#3A3830] hover:text-[#151312] z-10"
          aria-label="Close"
        >
          <X size={18} weight="thin" />
        </button>
        <div className="px-6 md:px-8 py-6 md:py-7">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Lesson {index + 1} · {lesson.minutes} min
          </div>
          <h3 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight text-[#151312]">
            {lesson.title}
          </h3>
          <p className="mt-4 text-[15px] leading-relaxed text-[#3A3830]">{body}</p>

          <div className="mt-4">
            <ListenTranslateBar text={listenText} testidPrefix={`lesson-explainer-${lesson.id}`} />
          </div>

          <div className="mt-5 rounded-xl border border-[#2D8659]/30 bg-[#2D8659]/8 px-4 py-3 text-sm text-[#215c40] leading-snug">
            {LESSON_SAVE_LINE}
          </div>

          <button
            type="button"
            onClick={() => { onAction?.(lesson.action); onClose(); }}
            data-testid={`academy-lesson-action-${lesson.id}`}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-3 font-mono-tech text-[11px] tracking-[0.28em] uppercase hover:opacity-90 transition-opacity"
          >
            <Sparkle size={13} weight="fill" /> {lesson.action?.label}
            <ArrowRight size={13} weight="bold" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const CONFETTI_COLORS = ["#B85F2A", "#2D8659", "#F9C784", "#E4572E", "#3A7D44", "#F2C94C"];

// A quick, dependency-free celebration burst over the progress bar.
function ConfettiBurst({ show }) {
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 h-0 z-20" data-testid="course-confetti">
      {Array.from({ length: 30 }).map((_, i) => {
        const x = (Math.random() * 2 - 1) * 260;
        const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
        const size = 6 + Math.random() * 7;
        return (
          <motion.span
            key={i}
            initial={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
            animate={{ opacity: 0, x, y: 70 + Math.random() * 50, rotate: Math.random() * 540 }}
            transition={{ duration: 1 + Math.random() * 0.5, delay: Math.random() * 0.15, ease: "easeOut" }}
            style={{ position: "absolute", left: "50%", top: 0, width: size, height: size * 0.6, background: color, borderRadius: 2 }}
          />
        );
      })}
    </div>
  );
}

// Shareable "Farm Marketing Graduate" badge shown once all lessons are done.
function GraduateBadge({ farmName }) {
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}${fpPath("/academy")}` : "";
  const who = farmName ? farmName : "Our farm";
  const shareText = `${who} just completed the FarmersPulse Marketing Crash Course — proud to be a Farm Marketing Graduate.`;

  async function share() {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Farm Marketing Graduate", text: shareText, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        toast.success("Share text copied to clipboard");
      }
    } catch { /* user cancelled */ }
  }

  function copyWebsiteBadge() {
    const html =
      `<a href="${shareUrl}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:8px;font-family:Georgia,serif;text-decoration:none;background:#151312;color:#F5EBDC;padding:10px 16px;border-radius:999px;font-size:14px;">` +
      `<span style="color:#F9C784;">&#9733;</span> Farm Marketing Graduate &middot; FarmersPulse</a>`;
    navigator.clipboard.writeText(html)
      .then(() => toast.success("Website badge copied", { description: "Paste the HTML anywhere on your site." }))
      .catch(() => {});
  }

  return (
    <div
      data-testid="academy-course-graduate"
      className="mt-5 rounded-2xl border border-[#F2C94C]/50 bg-gradient-to-br from-[#151312] to-[#2a1c12] text-[#F5EBDC] p-5 md:p-6 relative overflow-hidden"
    >
      <div className="flex items-start gap-4">
        <span className="shrink-0 grid place-items-center h-14 w-14 rounded-full bg-[#F9C784] text-[#151312] shadow-lg">
          <Medal size={30} weight="fill" />
        </span>
        <div className="min-w-0">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">
            Course complete · 12/12
          </div>
          <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mt-0.5">
            Farm Marketing Graduate
          </h3>
          <p className="text-sm text-[#F5EBDC]/80 mt-1 leading-snug">
            You finished the whole crash course. Show it off on your website or share the news.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={share}
              data-testid="graduate-share-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#F9C784] text-[#151312] px-4 py-2 font-mono-tech text-[10px] tracking-[0.26em] uppercase hover:opacity-90 transition-opacity"
            >
              <ShareNetwork size={13} weight="bold" /> Share
            </button>
            <button
              type="button"
              onClick={copyWebsiteBadge}
              data-testid="graduate-copy-badge-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#F5EBDC]/30 text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.26em] uppercase hover:bg-white/10 transition-colors"
            >
              <Copy size={13} weight="bold" /> Copy website badge
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CourseTab({ onAction }) {
  const [data, setData] = useState({ lessons: [], completed: [] });
  const [busy, setBusy] = useState(true);
  const [explain, setExplain] = useState(null); // { lesson, index } | null
  const [celebrate, setCelebrate] = useState(false);
  const [farmName, setFarmName] = useState("");

  useEffect(() => { load(); }, []);
  async function load() {
    setBusy(true);
    try {
      const { data } = await http.get("/academy/course");
      setData(data || { lessons: [], completed: [] });
    } finally { setBusy(false); }
    http.get("/farmers/farms/mine")
      .then((r) => { const f = (r?.data?.farms || [])[0]; if (f?.name) setFarmName(f.name); })
      .catch(() => {});
  }

  async function complete(id) {
    try {
      await http.post(`/academy/course/${id}/complete`);
      setData((d) => ({ ...d, completed: Array.from(new Set([...(d.completed||[]), id])) }));
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 1500);
    } catch (e) {
      alert("Sign in first — the Academy tracks your progress.");
    }
  }

  const totalMin = data.lessons.reduce((s, l) => s + (l.minutes||0), 0);
  const pct = data.lessons.length ? Math.round(100 * data.completed.length / data.lessons.length) : 0;
  const nextIdx = data.lessons.findIndex((l) => !data.completed.includes(l.id));
  const nextLesson = nextIdx >= 0 ? data.lessons[nextIdx] : null;

  return (
    <section data-testid="academy-course">
      <div className="rounded-3xl bg-white border border-black/10 p-5 md:p-6">
        <div className="flex flex-wrap items-baseline gap-4">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              45-60 minutes · free
            </div>
            <div className="font-serif-display text-2xl md:text-3xl mt-1">Marketing Crash Course</div>
          </div>
          <div className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]" data-testid="academy-course-progress">
            {data.completed.length}/{data.lessons.length} · {pct}% · {totalMin} min total
          </div>
        </div>
        <div className="relative mt-4">
          <div className="h-1.5 rounded-full bg-black/5 overflow-hidden">
            <div className="h-full bg-[#2D8659] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <ConfettiBurst show={celebrate} />
        </div>
        {!busy && nextLesson && (
          <button
            type="button"
            onClick={() => setExplain({ lesson: nextLesson, index: nextIdx })}
            data-testid="academy-course-continue"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase hover:opacity-90 transition-opacity"
          >
            <RocketLaunch size={13} weight="fill" />
            {data.completed.length ? "Continue" : "Start"}: Lesson {nextIdx + 1} — {nextLesson.title}
            <ArrowRight size={12} weight="bold" />
          </button>
        )}
        {!busy && !nextLesson && data.lessons.length > 0 && (
          <GraduateBadge farmName={farmName} />
        )}
      </div>

      <div className="mt-4 grid gap-3">
        {busy && <Loading />}
        {data.lessons.map((l, i) => {
          const done = data.completed.includes(l.id);
          return (
            <div
              key={l.id}
              data-testid={`academy-lesson-${l.id}`}
              className={`rounded-2xl bg-white border p-4 md:p-5 flex flex-wrap gap-4 items-start ${done ? "border-[#2D8659]/40" : "border-black/10"}`}
            >
              <div className={`flex-none w-8 h-8 rounded-full flex items-center justify-center font-mono-tech text-[11px] ${done ? "bg-[#2D8659] text-white" : "bg-[#F5EBDC] text-[#241209]"}`}>
                {done ? <CheckCircle size={14} weight="fill" /> : (i + 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif-display text-lg md:text-xl leading-tight">{l.title}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-0.5">
                  Lesson {i + 1} · {l.minutes} min
                </div>
                <div className="mt-2 text-sm text-[#3A3830]">
                  Action: <span className="font-medium text-[#151312]">{l.action?.label}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setExplain({ lesson: l, index: i })}
                  data-testid={`academy-lesson-info-${l.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
                >
                  <Info size={13} weight="bold" /> Show me how
                  <ArrowRight size={12} weight="bold" />
                </button>
              </div>
              <button
                onClick={() => complete(l.id)}
                disabled={done}
                data-testid={`academy-lesson-complete-${l.id}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${done ? "bg-[#2D8659]/15 text-[#2D8659] cursor-default" : "bg-[#151312] text-[#F5EBDC] hover:opacity-90"}`}
              >
                {done ? "Done" : "Mark complete"}
                {!done && <ArrowRight size={11} weight="bold" />}
              </button>
            </div>
          );
        })}
      </div>
      <LessonExplainerModal
        lesson={explain?.lesson}
        index={explain?.index ?? 0}
        onAction={onAction}
        onClose={() => setExplain(null)}
      />
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Growth Audit
// ══════════════════════════════════════════════════════════════════
function AuditTab({ onCompleteGoto }) {
  const [meta, setMeta] = useState({ choices: [], questions: [], farm_types: [] });
  const [farmType, setFarmType] = useState("vegetable");
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { load(); }, []);
  async function load() {
    setBusy(true);
    try {
      const { data } = await http.get("/academy/audit");
      setMeta(data);
    } finally { setBusy(false); }
  }

  const progress = useMemo(() => {
    const total = meta.questions.length || 1;
    return Math.round(100 * Object.keys(answers).length / total);
  }, [answers, meta.questions.length]);

  async function submit() {
    if (Object.keys(answers).length !== meta.questions.length) {
      setErr("Answer every question — even 'not yet' is fine.");
      return;
    }
    setErr(""); setSubmitting(true);
    try {
      const { data } = await http.post("/academy/audit", { farm_type: farmType, answers });
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sign in first — audits are saved to your profile.");
    } finally { setSubmitting(false); }
  }

  if (result) return <AuditResult result={result} onSeeRoadmap={() => onCompleteGoto("roadmap")} onSeeProjects={() => onCompleteGoto("projects")} />;

  return (
    <section data-testid="academy-audit">
      <div className="rounded-3xl bg-white border border-black/10 p-5 md:p-6">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
          {meta.questions.length} questions · about 4 minutes
        </div>
        <div className="font-serif-display text-2xl md:text-3xl mt-1">Farm Growth Audit</div>
        <p className="text-sm text-[#3A3830] mt-1">Answer honestly — we surface the 3 highest-value opportunities and recommend your first 30-day project.</p>

        <div className="mt-4">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mb-1">Your farm type</div>
          <div className="flex flex-wrap gap-1" data-testid="audit-farmtype-picker">
            {meta.farm_types.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setFarmType(t)}
                data-testid={`audit-farmtype-${t}`}
                className={`rounded-full border px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase transition ${farmType === t ? "bg-[#2D8659] text-white border-transparent" : "border-black/12 bg-white text-[#3A3830] hover:bg-black/5"}`}
              >
                {t.replace(/-/g, " ")}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 h-1.5 rounded-full bg-black/5 overflow-hidden">
        <div className="h-full bg-[#2D8659] transition-all" style={{ width: `${progress}%` }} data-testid="audit-progress" />
      </div>

      {busy && <Loading />}

      <div className="mt-4 grid gap-3">
        {meta.questions.map((q, i) => (
          <div key={q.id} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`audit-q-${q.id}`}>
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]">Q{i+1} · {q.area}</div>
            <div className="mt-1 text-[15px] leading-snug text-[#151312]">{q.q}</div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {meta.choices.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: c.key }))}
                  data-testid={`audit-a-${q.id}-${c.key}`}
                  className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase transition ${answers[q.id] === c.key ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "border-black/12 bg-white text-[#3A3830] hover:bg-black/5"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {err && <div className="mt-3 text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="audit-err">{err}</div>}

      <button
        onClick={submit}
        disabled={submitting || Object.keys(answers).length !== meta.questions.length}
        data-testid="audit-submit"
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase disabled:opacity-40 hover:opacity-90"
      >
        {submitting ? "Analysing…" : "See my top 3 opportunities"} <ArrowRight size={12} weight="bold" />
      </button>
    </section>
  );
}

function AuditResult({ result, onSeeRoadmap, onSeeProjects }) {
  const p = result.project;
  return (
    <section data-testid="academy-audit-result">
      <div className="rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6 md:p-8">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">
          Your top 3 opportunities · {result.farm_type.replace(/-/g," ")}
        </div>
        <div className="font-serif-display text-3xl mt-1 leading-tight">These will move the needle first.</div>
        <ol className="mt-4 space-y-3">
          {result.opportunities.map((o, i) => (
            <li key={o.id} className="flex gap-3" data-testid={`audit-opp-${i+1}`}>
              <span className="flex-none w-7 h-7 rounded-full bg-[#B85F2A] text-white flex items-center justify-center font-mono-tech text-[11px]">{i+1}</span>
              <div>
                <div className="font-serif-display text-lg leading-tight">{o.area}</div>
                <div className="text-[13px] text-[#EFE3CE] mt-0.5">{o.prompt}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {p && (
        <div className="mt-5 rounded-3xl bg-white border border-black/10 p-6" data-testid="audit-project-rec">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            Recommended first 30-day project
          </div>
          <div className="font-serif-display text-2xl md:text-3xl mt-1 leading-tight">{p.title}</div>
          <p className="text-[15px] text-[#3A3830] mt-2">{p.outcome}</p>
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-2">
            ~{p.estimated_hours} hours over 4 weeks · {p.milestones?.length} milestones
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={onSeeProjects} data-testid="audit-cta-projects" className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-95">
              Start this project <ArrowRight size={11} weight="bold" />
            </button>
            <button onClick={onSeeRoadmap} data-testid="audit-cta-roadmap" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/15 px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#241209] hover:bg-black/5">
              See full roadmap
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Roadmap
// ══════════════════════════════════════════════════════════════════
function RoadmapTab() {
  const [farmType, setFarmType] = useState("vegetable");
  const [farmTypes, setFarmTypes] = useState([]);
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => { http.get("/academy/audit").then((r) => setFarmTypes(r?.data?.farm_types || [])); }, []);
  useEffect(() => {
    setBusy(true);
    http.get("/academy/roadmap", { params: { farm_type: farmType } })
      .then((r) => setCards(r?.data?.cards || []))
      .finally(() => setBusy(false));
  }, [farmType]);

  return (
    <section data-testid="academy-roadmap">
      <div className="rounded-3xl bg-white border border-black/10 p-5">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Personalized Roadmap</div>
        <div className="font-serif-display text-2xl md:text-3xl mt-1">The next three plays for your farm.</div>
        <div className="mt-3 flex flex-wrap gap-1">
          {farmTypes.map((t) => (
            <button key={t} onClick={() => setFarmType(t)} data-testid={`roadmap-ft-${t}`}
              className={`rounded-full border px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase transition ${farmType === t ? "bg-[#2D8659] text-white border-transparent" : "border-black/12 bg-white text-[#3A3830] hover:bg-black/5"}`}
            >{t.replace(/-/g," ")}</button>
          ))}
        </div>
      </div>

      {busy && <Loading />}

      <div className="mt-4 grid md:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <div key={c.project_slug} className="rounded-2xl bg-white border border-black/10 p-5" data-testid={`roadmap-card-${c.project_slug}`}>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Play {i+1}</div>
            <div className="mt-1 font-serif-display text-lg md:text-xl leading-tight">{c.title}</div>
            <p className="mt-2 text-sm text-[#3A3830]">{c.outcome}</p>
            {c.why && <p className="mt-2 text-[12px] italic text-[#8A857A]">"{c.why}"</p>}
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-2">~{c.estimated_hours}h · 30 days</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
//  30-Day Projects
// ══════════════════════════════════════════════════════════════════
function ProjectsTab({ focus }) {
  const [blueprints, setBlueprints] = useState([]);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(true);
  const [active, setActive] = useState(null);   // opened blueprint or personal project
  const [highlight, setHighlight] = useState(null);

  useEffect(() => { load(); }, []);
  async function load() {
    setBusy(true);
    try {
      const [b, m] = await Promise.all([
        http.get("/academy/projects"),
        http.get("/academy/projects/mine/list").catch(() => ({ data: { projects: [] } })),
      ]);
      setBlueprints(b?.data?.projects || []);
      setMine(m?.data?.projects || []);
    } finally { setBusy(false); }
  }

  // When arriving via a lesson deep-link, jump to + highlight that project.
  useEffect(() => {
    if (busy || !focus) return;
    const el = document.querySelector(`[data-testid="blueprint-${focus}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlight(focus);
      const t = setTimeout(() => setHighlight(null), 3000);
      return () => clearTimeout(t);
    }
  }, [busy, focus]);

  async function start(slug) {
    try {
      const { data } = await http.post("/academy/projects/start", { slug });
      markLessonDone(PROJECT_TO_LESSON[slug]); // auto-tick the linked lesson
      await load();
      setActive(data.project);
    } catch (e) {
      alert("Sign in to start a project — your progress is saved to your farm.");
    }
  }

  if (active) return <ProjectWorkspace project={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <section data-testid="academy-projects">
      {mine.length > 0 && (
        <>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">My projects</div>
          <div className="grid md:grid-cols-2 gap-3">
            {mine.map((p) => (
              <button key={p.id} onClick={() => setActive(p)} data-testid={`mine-project-${p.slug}`}
                className="text-left rounded-2xl bg-white border border-[#2D8659]/30 p-4 hover:border-[#2D8659] transition"
              >
                <div className="font-serif-display text-lg leading-tight">{p.title}</div>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-1">
                  {p.status} · {p.progress_pct||0}%
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-black/5 overflow-hidden">
                  <div className="h-full bg-[#2D8659]" style={{ width: `${p.progress_pct||0}%` }} />
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mt-6 mb-2">
        All 30-day projects
      </div>
      {busy && <Loading />}
      <div className="grid md:grid-cols-2 gap-3">
        {blueprints.map((p) => (
          <div key={p.slug} className={`rounded-2xl bg-white border p-5 transition-all ${highlight === p.slug ? "border-[color:var(--np-brand,#B85F2A)] ring-2 ring-[color:var(--np-brand,#B85F2A)]/40" : "border-black/10"}`} data-testid={`blueprint-${p.slug}`}>
            <div className="font-serif-display text-lg md:text-xl leading-tight">{p.title}</div>
            <p className="mt-1 text-[13px] text-[#3A3830]">{p.outcome}</p>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-2">
              ~{p.estimated_hours}h · {p.duration_days} days · {p.milestones.length} milestones
            </div>
            <button onClick={() => start(p.slug)} data-testid={`start-${p.slug}`}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              Start project <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProjectWorkspace({ project, onBack }) {
  const [p, setP] = useState(project);

  async function toggle(week, step_index, done) {
    try {
      const { data } = await http.post(`/academy/projects/mine/${p.id}/step`, { week, step_index, done });
      setP((cur) => {
        const ms = [...cur.milestones];
        ms[week - 1].steps = ms[week - 1].steps.map((s, i) => i === step_index ? { ...s, done } : s);
        return { ...cur, milestones: ms, progress_pct: data.progress_pct, status: data.status };
      });
    } catch (e) { /* silent */ }
  }

  return (
    <section data-testid="project-workspace">
      <button onClick={onBack} className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        <ArrowLeft size={11} weight="bold" /> All projects
      </button>
      <div className="rounded-3xl bg-white border border-black/10 p-5 md:p-6">
        <div className="flex flex-wrap items-baseline gap-3">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">30-Day Project</div>
            <div className="font-serif-display text-2xl md:text-3xl mt-1 leading-tight">{p.title}</div>
            <p className="text-sm text-[#3A3830] mt-1">{p.outcome}</p>
          </div>
          <div className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]" data-testid="workspace-progress">
            {p.status} · {p.progress_pct||0}%
          </div>
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-black/5 overflow-hidden">
          <div className="h-full bg-[#2D8659] transition-all" style={{ width: `${p.progress_pct||0}%` }} />
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        {p.milestones.map((m) => {
          const total = m.steps.length;
          const done = m.steps.filter((s) => s.done).length;
          return (
            <div key={m.week} className="rounded-2xl bg-white border border-black/10 p-4 md:p-5" data-testid={`milestone-${m.week}`}>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#8A857A]">Week {m.week} · {done}/{total}</div>
              <div className="font-serif-display text-xl mt-1">{m.title}</div>
              <ul className="mt-3 space-y-1.5">
                {m.steps.map((s, i) => (
                  <li key={i}>
                    <label className="flex items-start gap-2 text-[14px] leading-snug text-[#241209] cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`step-${m.week}-${i}`}
                        checked={!!s.done}
                        onChange={(e) => toggle(m.week, i, e.target.checked)}
                        className="mt-1 h-4 w-4 accent-[#2D8659]"
                      />
                      <span className={s.done ? "line-through text-[#8A857A]" : ""}>{s.text}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {p.status === "launched" && (
        <div className="mt-5 rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6 flex items-center gap-3" data-testid="workspace-launched">
          <RocketLaunch size={22} weight="fill" className="text-[#F9C784]" />
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">Launched</div>
            <div className="font-serif-display text-xl leading-tight">Every step ticked — nice work.</div>
          </div>
        </div>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Asset Builder
// ══════════════════════════════════════════════════════════════════
function BuilderTab({ focus }) {
  const [builders, setBuilders] = useState([]);
  const [slug, setSlug] = useState(null);
  const [busy, setBusy] = useState(true);
  const [form, setForm] = useState({ farm_name: "", products: "", notes: "" });
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    http.get("/academy/builders").then((r) => {
      const list = r?.data?.builders || [];
      setBuilders(list);
      if (list.length) {
        setSlug(list.some((x) => x.slug === focus) ? focus : list[0].slug);
      }
    }).finally(() => setBusy(false));
    // Pre-fill from the farmer's saved farm so drafts are personalized with zero typing.
    http.get("/farmers/farms/mine").then((r) => {
      const farms = r?.data?.farms || [];
      if (!farms.length) return;
      const farm = farms[0];
      const name = (farm.name || "").trim();
      const crops = farms.flatMap((f) => f.crops || []).filter(Boolean);
      const region = [farm.village, farm.district, farm.state, farm.country].filter(Boolean).join(", ");
      const season = seasonFor(farm.latitude);
      let note = "";
      if (region && season) note = `We farm near ${region}, and it's currently ${season} here — keep the tone local and seasonal.`;
      else if (region) note = `We farm near ${region} — keep the tone local.`;
      else if (season) note = `It's currently ${season} here — keep the tone seasonal.`;
      setForm((f) => ({
        ...f,
        farm_name: f.farm_name || name,
        products: f.products || crops.join(", "),
        notes: f.notes || note,
      }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function generate() {
    if (!slug) return;
    setDrafting(true); setDraft("");
    try {
      const { data } = await http.post(`/academy/builders/${slug}/draft`, form);
      setDraft(data?.draft || "");
      markLessonDone(BUILDER_TO_LESSON[slug]); // auto-tick the linked lesson
    } catch (e) {
      setDraft("Something broke drafting your asset — try again in a minute.");
    } finally { setDrafting(false); }
  }

  const b = builders.find((x) => x.slug === slug);

  return (
    <section data-testid="academy-builder">
      <div className="rounded-3xl bg-white border border-black/10 p-5">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Marketing Asset Builder</div>
        <div className="font-serif-display text-2xl md:text-3xl mt-1">AI-drafted, farmer-approved.</div>
        <p className="text-sm text-[#3A3830] mt-1">Pick an asset, give it a few facts, and Claude drafts it in your voice. Nothing is invented — you always approve before it ships.</p>
        <div className="mt-3 flex flex-wrap gap-1" data-testid="builder-picker">
          {busy && <Loading />}
          {builders.map((x) => (
            <button key={x.slug} onClick={() => { setSlug(x.slug); setDraft(""); }} data-testid={`builder-${x.slug}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase transition ${slug === x.slug ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "border-black/12 bg-white text-[#3A3830] hover:bg-black/5"}`}
            >{x.title}</button>
          ))}
        </div>
      </div>

      {b && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-black/10 p-5">
            <div className="font-serif-display text-xl">{b.title}</div>
            <div className="text-[13px] text-[#3A3830] mt-1">{b.output}</div>
            <div className="mt-4 grid gap-3">
              <Field label="Farm name">
                <input value={form.farm_name} onChange={(e) => setForm((f) => ({ ...f, farm_name: e.target.value }))} data-testid="builder-farm-name" className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" placeholder="e.g. Bloomfield Family Farm" />
              </Field>
              <Field label="Products / context">
                <input value={form.products} onChange={(e) => setForm((f) => ({ ...f, products: e.target.value }))} data-testid="builder-products" className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" placeholder="e.g. Heirloom tomatoes, CSA boxes, pasture eggs" />
              </Field>
              <Field label="Anything else the AI should know">
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="builder-notes" rows={3} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" placeholder="Voice preferences, story, upcoming events…" />
              </Field>
              <button onClick={generate} disabled={drafting} data-testid="builder-generate"
                className="inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase disabled:opacity-60"
              >
                <Sparkle size={12} weight="fill" />
                {drafting ? "Drafting…" : "Draft with AI"}
              </button>
            </div>
          </div>
          <div className="rounded-2xl bg-[#F9F5EC] border border-black/10 p-5 min-h-[240px]" data-testid="builder-output">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#8A857A] mb-2">Draft</div>
            {drafting && <Loading />}
            {!drafting && !draft && <p className="text-sm text-[#8A857A]">Your AI draft will appear here.</p>}
            {draft && <pre className="whitespace-pre-wrap font-serif-display text-[15px] leading-relaxed text-[#241209]">{draft}</pre>}
          </div>
        </div>
      )}
    </section>
  );
}

// Meteorological season for the farm's hemisphere (from latitude).
function seasonFor(lat) {
  const m = new Date().getMonth(); // 0-11
  const north = ["Winter", "Winter", "Spring", "Spring", "Spring", "Summer", "Summer", "Summer", "Autumn", "Autumn", "Autumn", "Winter"];
  const s = north[m];
  if (lat == null || lat >= 0) return s;
  return { Winter: "Summer", Summer: "Winter", Spring: "Autumn", Autumn: "Spring" }[s];
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A] block mb-1">{label}</span>
      {children}
    </label>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A]">
      <CircleNotch size={14} className="animate-spin" /> Loading…
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Phase 2 + 3 · Dashboard · Campaign Vault · Calendar · Brand Voice
// ══════════════════════════════════════════════════════════════════
function DashboardTab({ onGoto }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => { http.get("/academy/dashboard").then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setBusy(false)); }, []);
  if (busy) return <Loading />;
  if (!d) return <div className="text-sm text-[#8A857A]">Sign in to see your dashboard.</div>;

  return (
    <section className="grid gap-4" data-testid="academy-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Lessons done" value={`${d.lessons_completed}/${d.total_lessons}`} />
        <Stat label="Projects launched" value={d.projects_launched} />
        <Stat label="Assets saved" value={d.assets_saved} />
        <Stat label="Active project" value={d.active_project ? "1" : "0"} />
      </div>
      {d.active_project && (
        <div className="rounded-3xl bg-white border border-[#2D8659]/30 p-5" data-testid="dash-active">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Current project</div>
          <div className="font-serif-display text-2xl mt-1 leading-tight">{d.active_project.title}</div>
          <div className="mt-2 h-1.5 rounded-full bg-black/5 overflow-hidden"><div className="h-full bg-[#2D8659]" style={{ width: `${d.active_project.progress_pct||0}%` }} /></div>
          {d.next_task && (
            <div className="mt-3 text-sm text-[#241209]"><strong>Next task:</strong> {d.next_task.text} <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#8A857A] ml-1">Week {d.next_task.week}</span></div>
          )}
          <button onClick={() => onGoto("projects")} data-testid="dash-open-project" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Open project <ArrowRight size={11} weight="bold" /></button>
        </div>
      )}
      {d.recommended_project && !d.active_project && (
        <div className="rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6" data-testid="dash-recommend">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">From your last audit</div>
          <div className="font-serif-display text-2xl mt-1 leading-tight">{d.recommended_project.title}</div>
          <p className="text-[13px] text-[#EFE3CE] mt-2">{d.recommended_project.outcome}</p>
          <button onClick={() => onGoto("projects")} data-testid="dash-recommend-cta" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Start this project <ArrowRight size={11} weight="bold" /></button>
        </div>
      )}
      {!d.active_project && !d.recommended_project && (
        <div className="rounded-3xl bg-white border border-black/10 p-6 text-sm text-[#3A3830]" data-testid="dash-empty">
          Take the <button onClick={() => onGoto("audit")} className="underline text-[color:var(--np-brand,#B85F2A)]">Growth Audit</button> and we'll recommend your first 30-day project.
        </div>
      )}
    </section>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-2xl bg-white border border-black/10 p-4">
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]">{label}</div>
    <div className="mt-1 font-serif-display text-2xl">{value ?? 0}</div>
  </div>
);

function VaultTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [form, setForm] = useState({ title: "", goal: "", audience: "", products: "", offer: "", dates: "", results: "", notes: "" });
  const load = () => { setBusy(true); http.get("/academy/campaigns").then((r) => setItems(r.data.campaigns || [])).catch(() => setItems([])).finally(() => setBusy(false)); };
  useEffect(load, []);
  async function save(e) { e.preventDefault(); try { await http.post("/academy/campaigns", form); setForm({ title: "", goal: "", audience: "", products: "", offer: "", dates: "", results: "", notes: "" }); load(); } catch { alert("Sign in first."); } }
  async function repeat(id) { try { await http.post(`/academy/campaigns/${id}/repeat`); load(); } catch {} }

  return (
    <section data-testid="academy-vault">
      <form onSubmit={save} className="rounded-3xl bg-white border border-black/10 p-5 grid md:grid-cols-2 gap-3" data-testid="vault-form">
        <div className="md:col-span-2 font-serif-display text-xl">Save a campaign</div>
        {["title","goal","audience","products","offer","dates","results","notes"].map((k) => (
          <input key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={k[0].toUpperCase()+k.slice(1)} data-testid={`vault-${k}`} className="rounded-lg border border-black/15 px-3 py-2 text-sm" />
        ))}
        <button type="submit" data-testid="vault-save" className="md:col-span-2 justify-self-start inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Save <ArrowRight size={11} weight="bold" /></button>
      </form>
      <div className="mt-6 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#8A857A]">Saved campaigns</div>
      {busy && <Loading />}
      <div className="mt-2 grid md:grid-cols-2 gap-3">
        {items.map((c) => (
          <div key={c.id} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`camp-${c.id}`}>
            <div className="font-serif-display text-lg">{c.title}</div>
            {c.dates && <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-1">{c.dates}</div>}
            {c.goal && <div className="text-[13px] mt-1"><strong>Goal:</strong> {c.goal}</div>}
            {c.results && <div className="text-[13px] mt-1"><strong>Results:</strong> {c.results}</div>}
            <button onClick={() => repeat(c.id)} data-testid={`camp-repeat-${c.id}`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase">Repeat this year</button>
          </div>
        ))}
      </div>
    </section>
  );
}

function CalendarTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { http.get("/academy/calendar").then((r) => setRows(r.data.calendar || [])); }, []);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return (
    <section data-testid="academy-calendar">
      <div className="grid md:grid-cols-3 gap-3">
        {rows.map((c) => (
          <div key={c.month} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`cal-${c.month}`}>
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{months[c.month-1]}</div>
            <div className="font-serif-display text-lg mt-1 leading-tight">{c.beat}</div>
            <div className="text-[13px] text-[#3A3830] mt-1"><strong>Do:</strong> {c.action}</div>
            <div className="mt-2 flex flex-wrap gap-1">{(c.tags||[]).map((t) => <span key={t} className="font-mono-tech text-[9px] tracking-[0.24em] uppercase bg-[#F9F5EC] px-2 py-0.5 rounded-full text-[#7A3E12]">{t}</span>)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BrandVoiceTab() {
  const [v, setV] = useState({ farm_name: "", story: "", values: "", products: "", practices: "", customer_types: "", tone: "warm, honest, farmer-to-customer", words_use: "", words_avoid: "", approved_claims: "", languages: "en" });
  const [busy, setBusy] = useState(true);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    http.get("/academy/brand-voice").then((r) => {
      const bv = r.data.brand_voice || {};
      setV((cur) => ({ ...cur, ...bv, words_use: (bv.words_use||[]).join(", "), words_avoid: (bv.words_avoid||[]).join(", "), approved_claims: (bv.approved_claims||[]).join(", "), languages: (bv.languages||["en"]).join(", ") }));
    }).finally(() => setBusy(false));
  }, []);
  async function save(e) {
    e.preventDefault();
    const payload = { ...v,
      words_use: v.words_use.split(",").map((s) => s.trim()).filter(Boolean),
      words_avoid: v.words_avoid.split(",").map((s) => s.trim()).filter(Boolean),
      approved_claims: v.approved_claims.split(",").map((s) => s.trim()).filter(Boolean),
      languages: v.languages.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try { await http.post("/academy/brand-voice", payload); setSaved(true); setTimeout(() => setSaved(false), 3000); } catch { alert("Sign in first."); }
  }
  if (busy) return <Loading />;
  return (
    <section data-testid="academy-voice">
      <form onSubmit={save} className="rounded-3xl bg-white border border-black/10 p-5 grid md:grid-cols-2 gap-3" data-testid="voice-form">
        <div className="md:col-span-2 font-serif-display text-xl">Train your brand voice</div>
        <p className="md:col-span-2 text-[13px] text-[#3A3830]">Give the Asset Builder your farm's real facts + voice. It'll draft consistent emails, captions and copy — never inventing claims.</p>
        {[
          ["farm_name","Farm name"],["tone","Preferred tone"],
          ["story","Farm story (short)"],["values","Values you stand for"],
          ["products","Your products"],["practices","Farming practices"],
          ["customer_types","Ideal customers"],["languages","Languages (comma-separated)"],
          ["words_use","Words to use (comma)"],["words_avoid","Words to avoid (comma)"],
          ["approved_claims","Approved claims (comma)"],
        ].map(([k, label]) => (
          <label key={k} className="block">
            <span className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A] block mb-1">{label}</span>
            {k === "story" || k === "values" || k === "practices"
              ? <textarea rows={3} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`voice-${k}`} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" />
              : <input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`voice-${k}`} className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm" />}
          </label>
        ))}
        <div className="md:col-span-2 flex items-center gap-3">
          <button type="submit" data-testid="voice-save" className="inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase">Save brand voice <CheckCircle size={12} weight="fill" /></button>
          {saved && <span data-testid="voice-saved" className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#2D8659]">Saved — assets will now use this voice.</span>}
        </div>
      </form>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
//  Phase 4 · Project Recommender AI · Customer Journey Mapper
// ══════════════════════════════════════════════════════════════════
function RecommendTab({ onGoto }) {
  const [rec, setRec] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [starting, setStarting] = useState(null);

  useEffect(() => { loadHistory(); }, []);
  async function loadHistory() {
    try { const { data } = await http.get("/academy/recommend/history"); setHistory(data?.history || []); }
    catch { setHistory([]); }
  }

  async function pull() {
    setBusy(true); setErr(""); setRec(null);
    try {
      const { data } = await http.get("/academy/recommend");
      setRec(data?.recommendation || null);
      await loadHistory();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sign in first — the recommender uses your audit + active work to pick the next play.");
    } finally { setBusy(false); }
  }

  async function start(slug) {
    setStarting(slug);
    try {
      await http.post("/academy/projects/start", { slug });
      onGoto("projects");
    } catch { alert("Sign in to start a project."); }
    finally { setStarting(null); }
  }

  const primary = rec?.primary;
  const alts = (rec?.alternatives || []).filter(Boolean);

  return (
    <section data-testid="academy-recommend">
      <div className="rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6 md:p-8">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">Project Recommender AI</div>
        <div className="font-serif-display text-3xl md:text-4xl mt-1 leading-tight">What should I work on next?</div>
        <p className="mt-2 text-[15px] text-[#EFE3CE] max-w-2xl">One primary + two alternatives, picked from your last audit, current season, and what you've already launched. Powered by Claude Sonnet with a rules-based fallback.</p>
        <button onClick={pull} disabled={busy} data-testid="recommend-ask"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2.5 font-mono-tech text-[11px] tracking-[0.28em] uppercase disabled:opacity-60 hover:opacity-95">
          <Sparkle size={12} weight="fill" />{busy ? "Thinking…" : (rec ? "Ask again" : "Recommend a project")}
        </button>
      </div>

      {err && <div className="mt-3 text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="recommend-err">{err}</div>}
      {busy && <div className="mt-4"><Loading /></div>}

      {rec && (
        <div className="mt-6 grid gap-4">
          {primary && (
            <div className="rounded-3xl bg-white border border-[#2D8659]/40 p-6" data-testid="rec-primary">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#2D8659]">Primary pick</div>
              <div className="font-serif-display text-2xl md:text-3xl mt-1 leading-tight">{primary.title}</div>
              {primary.outcome && <p className="text-[15px] text-[#3A3830] mt-2">{primary.outcome}</p>}
              {primary.why && <p className="mt-2 text-[13px] italic text-[#7A3E12]" data-testid="rec-primary-why">"{primary.why}"</p>}
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-2">~{primary.estimated_hours}h · 30 days</div>
              <button onClick={() => start(primary.slug)} disabled={starting === primary.slug} data-testid="rec-start-primary"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-60">
                {starting === primary.slug ? "Starting…" : "Start this project"} <ArrowRight size={11} weight="bold" />
              </button>
            </div>
          )}
          {alts.length > 0 && (
            <div className="grid md:grid-cols-2 gap-3" data-testid="rec-alts">
              {alts.map((a, i) => (
                <div key={a.slug} className="rounded-2xl bg-white border border-black/10 p-5" data-testid={`rec-alt-${i}`}>
                  <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Alternative {i + 1}</div>
                  <div className="font-serif-display text-xl mt-1 leading-tight">{a.title}</div>
                  {a.why && <p className="mt-2 text-[13px] italic text-[#7A3E12]">"{a.why}"</p>}
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mt-2">~{a.estimated_hours}h</div>
                  <button onClick={() => start(a.slug)} disabled={starting === a.slug} data-testid={`rec-start-alt-${i}`}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase disabled:opacity-60">
                    {starting === a.slug ? "Starting…" : "Start"} <ArrowRight size={11} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {rec.rationale && (
            <div className="rounded-2xl bg-[#F9F5EC] border border-black/10 p-4 text-[13px] text-[#241209]" data-testid="rec-rationale">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#8A857A] mb-1">Why these three</div>
              {rec.rationale}
            </div>
          )}
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#8A857A]" data-testid="rec-source">
            Source · {rec.source === "ai" ? "Claude Sonnet 4.6" : "rules-based fallback"}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-8" data-testid="rec-history">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#8A857A] mb-2">Recent picks</div>
          <div className="grid md:grid-cols-2 gap-3">
            {history.map((h) => (
              <div key={h.id} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`rec-hist-${h.id}`}>
                <div className="flex items-baseline gap-2">
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">{new Date(h.created_at).toLocaleDateString()}</div>
                  <div className="ml-auto font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#8A857A]">{h.source}</div>
                </div>
                {h.primary && (
                  <>
                    <div className="font-serif-display text-lg mt-1 leading-tight">{h.primary.title}</div>
                    {h.primary.why && <p className="mt-1 text-[12px] italic text-[#7A3E12]">"{h.primary.why}"</p>}
                    <button onClick={() => start(h.primary.slug)} data-testid={`rec-hist-start-${h.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:opacity-90">
                      Replay <ArrowRight size={10} weight="bold" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function JourneyTab({ onGoto }) {
  const [d, setD] = useState({ stages: [], coverage: {}, missing: [], covered_count: 0, total: 10 });
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(false);

  const load = () => {
    setBusy(true); setErr("");
    http.get("/academy/journey")
      .then((r) => setD(r.data))
      .catch(() => setD({ stages: [], coverage: {}, missing: [], covered_count: 0, total: 10 }))
      .finally(() => setBusy(false));
    http.get("/academy/journey/analytics")
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  };
  useEffect(load, []);

  async function toggle(stageId, next) {
    const nextCov = { ...(d.coverage || {}), [stageId]: next };
    setD((cur) => {
      const covered_count = cur.stages.filter((s) => nextCov[s.id]).length;
      const missing = cur.stages.filter((s) => !nextCov[s.id]);
      return { ...cur, coverage: nextCov, covered_count, missing };
    });
    setSaving(true);
    try {
      await http.post("/academy/journey", { coverage: nextCov });
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Sign in first — the journey mapper saves per farmer.");
    } finally { setSaving(false); }
  }

  function jump(action) {
    if (!action) return;
    if (action.tool_to) { window.location.href = action.tool_to; return; }
    if (action.builder) { onGoto("builder"); return; }
    if (action.project_slug) { onGoto("projects"); return; }
  }

  if (busy) return <Loading />;
  const covered = d.covered_count || 0;
  const total = d.total || 10;
  const pct = total ? Math.round(100 * covered / total) : 0;

  return (
    <section data-testid="academy-journey">
      <div className="rounded-3xl bg-white border border-black/10 p-5 md:p-6">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Customer Journey Mapper</div>
        <div className="font-serif-display text-2xl md:text-3xl mt-1 leading-tight">10 stages · from stranger to community anchor.</div>
        <p className="text-[15px] text-[#3A3830] mt-2 max-w-2xl">Mark the stages you already cover. We surface the gaps + the Academy tools that plug them.</p>
        <div className="mt-3 flex items-baseline gap-3">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]" data-testid="journey-progress">{covered}/{total} · {pct}%</div>
          {saving && <span className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#8A857A]">Saving…</span>}
          {!saving && savedAt > 0 && <span data-testid="journey-saved" className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#2D8659]">Saved</span>}
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-black/5 overflow-hidden"><div className="h-full bg-[#2D8659] transition-all" style={{ width: `${pct}%` }} /></div>
        {err && <div className="mt-3 text-[11px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="journey-err">{err}</div>}
      </div>

      {/* Canvas — 10 stages */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="journey-canvas">
        {d.stages.map((s) => {
          const on = !!d.coverage?.[s.id];
          return (
            <div key={s.id} data-testid={`journey-stage-${s.id}`}
              className={`rounded-2xl border p-4 transition ${on ? "bg-[#0F3D2A] text-[#F5EBDC] border-transparent" : "bg-white text-[#241209] border-black/10"}`}>
              <div className={`font-mono-tech text-[10px] tracking-[0.28em] uppercase ${on ? "text-[#F9C784]" : "text-[color:var(--np-brand,#B85F2A)]"}`}>Stage {s.n}</div>
              <div className="font-serif-display text-lg mt-1 leading-tight">{s.title}</div>
              <p className={`text-[12px] mt-1 ${on ? "text-[#EFE3CE]" : "text-[#3A3830]"}`}>{s.goal}</p>
              <label className={`mt-3 flex items-center gap-2 text-[12px] font-mono-tech tracking-[0.24em] uppercase cursor-pointer ${on ? "text-[#F9C784]" : "text-[#3A3830]"}`}>
                <input type="checkbox" checked={on} onChange={(e) => toggle(s.id, e.target.checked)} data-testid={`journey-toggle-${s.id}`} className="h-4 w-4 accent-[#2D8659]" />
                {on ? "Covered" : "Mark as covered"}
              </label>
            </div>
          );
        })}
      </div>

      {/* Missing stages + suggested tools */}
      {d.missing && d.missing.length > 0 && (
        <div className="mt-6 rounded-3xl bg-[#F9F5EC] border border-[#B85F2A]/30 p-5" data-testid="journey-gaps">
          <div className="flex items-center gap-2">
            <Warning size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              {d.missing.length} stage{d.missing.length === 1 ? "" : "s"} to build
            </div>
          </div>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            {d.missing.map((s) => (
              <div key={s.id} className="rounded-2xl bg-white border border-black/10 p-4" data-testid={`journey-gap-${s.id}`}>
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">Stage {s.n} · missing</div>
                <div className="font-serif-display text-lg mt-1 leading-tight">{s.title}</div>
                <p className="text-[12px] text-[#3A3830] mt-1">{s.goal}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(s.tools || []).map((t, i) => (
                    <button key={i} onClick={() => jump(t)} data-testid={`journey-tool-${s.id}-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-90">
                      {t.label} <ArrowRight size={11} weight="bold" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {covered === total && (
        <div className="mt-6 rounded-3xl bg-[#0F3D2A] text-[#F5EBDC] p-6 flex items-center gap-3" data-testid="journey-complete">
          <CheckCircle size={22} weight="fill" className="text-[#F9C784]" />
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#F9C784]">Full coverage</div>
            <div className="font-serif-display text-xl leading-tight">Every stage is covered — now measure retention.</div>
          </div>
        </div>
      )}

      {analytics && analytics.total_farms > 0 && (
        <div className="mt-6" data-testid="journey-analytics">
          <button type="button" onClick={() => setShowAnalytics((s) => !s)} data-testid="journey-analytics-toggle"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#241209] hover:bg-black/5">
            {showAnalytics ? "Hide" : "Show"} platform benchmarks · {analytics.total_farms} farm{analytics.total_farms === 1 ? "" : "s"}
          </button>
          {showAnalytics && (
            <div className="mt-3 rounded-2xl bg-white border border-black/10 p-4" data-testid="journey-analytics-body">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#8A857A] mb-1">Which stages farmers skip most often</div>
              <div className="grid gap-1.5">
                {analytics.stages.map((s) => (
                  <div key={s.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 text-[12px]" data-testid={`journey-anal-${s.id}`}>
                    <span className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#8A857A] w-16">Stage {s.n}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate">{s.title}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-black/5 overflow-hidden min-w-[60px]">
                        <div className="h-full bg-[#2D8659]" style={{ width: `${s.covered_pct}%` }} />
                      </div>
                    </div>
                    <span className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#241209] tabular-nums">{s.covered_pct}% covered</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] italic text-[#7A3E12]">Sorted by most-skipped. Use this to see which Academy tools to invest in next.</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

